/* Read-only progressive enhancement. Never issues cart or signup requests. */
(() => {
  const order = ['birthday', 'thank-you', 'thinking-of-you', 'congratulations', 'wedding', 'anniversary', 'love-and-romance', 'sympathy', 'get-well', 'new-baby', 'graduation', 'holiday'];
  const normalize = value => {
    const id = String(value || '').trim().toLowerCase().replaceAll('&', 'and').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '');
    return id === 'love' ? 'love-and-romance' : id === 'get-well-soon' ? 'get-well' : id;
  };
  const compare = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' });
  class CardCatalog extends HTMLElement {
    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controls = this.querySelector('[data-controls]');
      this.status = this.querySelector('[data-status]');
      this.results = this.querySelector('[data-results]');
      this.selected = new Set();
      this.controls.hidden = false;
      this.status.textContent = 'Loading all cards…';
      try {
        const nodes = [...this.querySelectorAll('[data-card]')];
        // Always start hydration at page 1, even on a directly visited later page.
        const first = new URL(location.href);
        first.searchParams.delete('page');
        first.searchParams.set('section_id', this.dataset.section);
        let next = first.href;
        const seen = new Set();
        while (next) {
          const url = new URL(next, location.href);
          url.searchParams.set('section_id', this.dataset.section);
          if (url.origin !== location.origin || seen.has(url.href)) throw new Error('Invalid catalog pagination');
          seen.add(url.href);
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Catalog unavailable');
          const page = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('mm-card-catalog');
          if (!page) throw new Error('Missing catalog');
          nodes.push(...page.querySelectorAll('[data-card]'));
          next = page.querySelector('[data-next]')?.getAttribute('href');
        }
        const unique = new Map();
        for (const node of nodes) {
          const occasions = new Map();
          for (const ref of JSON.parse(node.dataset.occasions || '[]')) {
            const id = normalize(ref.id);
            if (id && ref.label?.trim() && !occasions.has(id)) occasions.set(id, { id, label: ref.label.trim() });
          }
          if (!occasions.size) occasions.set('other', { id: 'other', label: 'Other' });
          unique.set(node.dataset.id, { node, id: node.dataset.id, title: node.dataset.title, available: node.dataset.available === 'true', occasions: [...occasions.values()], search: `${node.dataset.search} ${[...occasions.values()].map(o => o.label).join(' ')}`.toLowerCase() });
        }
        this.cards = [...unique.values()].sort((a,b) => compare(a.title,b.title) || a.id.localeCompare(b.id));
        if (this.cards.length !== Number(this.dataset.total)) throw new Error('Incomplete catalog');
        const occasions = new Map();
        this.cards.forEach(card => card.occasions.forEach(o => occasions.set(o.id, o)));
        const rank = id => id === 'other' ? 999 : order.includes(id) ? order.indexOf(id) : order.length;
        this.occasions = [...occasions.values()].sort((a,b) => rank(a.id)-rank(b.id) || compare(a.label,b.label) || a.id.localeCompare(b.id));
        this.filters = this.querySelector('[data-filters]');
        for (const occasion of this.occasions) {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.occasion = occasion.id;
          button.textContent = `${occasion.label} (${this.cards.filter(c => c.occasions.some(o => o.id === occasion.id)).length})`;
          button.setAttribute('aria-pressed', 'false');
          button.addEventListener('click', () => {
            if (this.selected.has(occasion.id)) this.selected.delete(occasion.id); else this.selected.add(occasion.id);
            this.render();
          });
          this.filters.append(button);
        }
        this.querySelector('[data-search-input]').addEventListener('input', () => this.render());
        this.querySelector('[data-clear]').addEventListener('click', () => {
          this.selected.clear(); this.querySelector('[data-search-input]').value = ''; this.render();
        });
        this.controls.disabled = false;
        this.querySelector('[data-pagination]').hidden = true;
        this.render();
      } catch (_) {
        this.status.textContent = 'Search and occasion filters could not load the complete catalog. Browse the cards and page links below, or reload to try again.';
      }
    }
    render() {
      const query = this.querySelector('[data-search-input]').value.trim().toLowerCase();
      const cards = this.cards.filter(c => (!query || c.search.includes(query)) && (!this.selected.size || c.occasions.some(o => this.selected.has(o.id))));
      this.filters.querySelectorAll('button').forEach(b => b.setAttribute('aria-pressed', String(this.selected.has(b.dataset.occasion))));
      this.status.textContent = `${cards.length} ${cards.length === 1 ? 'card' : 'cards'}${this.selected.size > 1 ? ' · Matching any selected occasion' : ''}`;
      this.results.replaceChildren();
      const grid = items => {
        const element = document.createElement('div'); element.className = 'mm-catalog-grid';
        items.forEach(c => element.append(c.node.cloneNode(true))); return element;
      };
      const shelf = (title, items) => {
        if (!items.length) return;
        const section = document.createElement('section'); section.className = 'mm-catalog-shelf';
        const h = document.createElement('h2'); h.textContent = title;
        const row = grid(items); row.classList.add('mm-catalog-row');
        row.tabIndex = 0; row.setAttribute('role', 'region'); row.setAttribute('aria-label', title);
        section.append(h, row); this.results.append(section);
      };
      if (!cards.length) {
        const p = document.createElement('p');
        p.textContent = this.cards.length ? 'No cards found. Try another word or clear your occasion filters.' : 'No cards are available to browse yet. Please check back soon.';
        this.results.append(p);
      } else if (query || this.selected.size) this.results.append(grid(cards));
      else {
        this.occasions.forEach(o => shelf(o.label, cards.filter(c => c.available && c.occasions.some(ref => ref.id === o.id))));
        shelf('Coming soon', cards.filter(c => !c.available));
      }
    }
  }
  if (!customElements.get('mm-card-catalog')) customElements.define('mm-card-catalog', CardCatalog);
})();
